import type { ManualIssueListItem } from '@accestra/types';
import { getWcagCriterionByLabel, parseWcagVersionOption } from './wcag-criteria';

export const UTF8_BOM = '\uFEFF';

const METADATA_COLUMNS = ['Section', 'Label', 'Value'] as const;

const ISSUE_EXPORT_COLUMNS = [
  'Source',
  'Site Name',
  'Site URL',
  'Issue Type',
  'Review Batch',
  'Title',
  'Description',
  'WCAG Version',
  'WCAG Target Level',
  'Success Criterion',
  'Success Criterion Level',
  'Severity',
  'Status',
  'Analyst Notes',
  'Page URL',
  'Selector',
  'Recommendation',
  'Created At',
  'Updated At',
] as const;

function escapeCsvValue(value: string): string {
  if (value.includes('"') || value.includes(',') || value.includes('\n') || value.includes('\r')) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
}

function formatIssueType(issueType: ManualIssueListItem['issueType'] | undefined): string {
  return issueType === 'usability_issue' ? 'Usability Issue' : 'WCAG Issue';
}

function formatSeverity(severity: ManualIssueListItem['severity']): string {
  switch (severity) {
    case 'critical':
      return 'Critical';
    case 'serious':
      return 'Serious';
    case 'moderate':
      return 'Moderate';
    case 'minor':
    default:
      return 'Minor';
  }
}

function formatStatus(status: ManualIssueListItem['status']): string {
  switch (status) {
    case 'in_progress':
      return 'In Progress';
    case 'resolved':
      return 'Fixed';
    case 'open':
    default:
      return 'Open';
  }
}

function formatDateValue(value: Date | string): string {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

function formatWcagTargetLevel(value: ManualIssueListItem['wcagVersion']): string {
  return parseWcagVersionOption(value)?.level ?? '';
}

function formatSuccessCriterionLevel(value: ManualIssueListItem['wcagCriterion']): string {
  return getWcagCriterionByLabel(value)?.level ?? '';
}

function slugifyFilePart(value: string): string {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalized || 'workspace';
}

function serializeCsvRows(rows: string[][]): string {
  return rows.map((row) => row.map((value) => escapeCsvValue(value)).join(',')).join('\r\n');
}

function buildMetadataRows(date: Date): string[][] {
  return [
    [...METADATA_COLUMNS],
    ['Export metadata', 'File type', 'AbleComply manual accessibility findings CSV'],
    ['Export metadata', 'Generated at', formatDateValue(date)],
    ['Legend', 'Metadata section', 'Explains the export file and field meanings for spreadsheet users.'],
    ['Legend', 'Findings section', 'One row per manual accessibility finding used for remediation coordination.'],
    ['Legend', 'WCAG Version', 'Reviewer-selected WCAG release and target conformance level.'],
    ['Legend', 'WCAG Target Level', 'Target conformance level from the selected WCAG version.'],
    ['Legend', 'Success Criterion', 'WCAG success criterion label selected or entered by the reviewer.'],
    ['Legend', 'Success Criterion Level', 'The conformance level of the selected WCAG success criterion when known.'],
    ['Legend', 'Review Batch', 'Optional lightweight grouping for findings from an intake pass.'],
    ['Legend', 'Page URL', 'Affected page, route, or URL that needs remediation.'],
    ['Legend', 'Recommendation', 'Suggested remediation guidance captured during structured intake.'],
  ];
}

export function buildManualIssuesCsv(issues: ManualIssueListItem[], date: Date = new Date()): string {
  const rows = issues.map((issue) => ([
    'Manual',
    issue.siteName,
    issue.siteBaseUrl,
    formatIssueType(issue.issueType),
    issue.reviewBatch ?? '',
    issue.title,
    issue.description,
    issue.wcagVersion ?? '',
    formatWcagTargetLevel(issue.wcagVersion),
    issue.wcagCriterion ?? '',
    formatSuccessCriterionLevel(issue.wcagCriterion),
    formatSeverity(issue.severity),
    formatStatus(issue.status),
    '',
    issue.affectedPageUrl ?? '',
    '',
    issue.recommendedFix ?? '',
    formatDateValue(issue.createdAt),
    formatDateValue(issue.updatedAt),
  ]));

  return `${UTF8_BOM}${serializeCsvRows([
    ...buildMetadataRows(date),
    [],
    ['Findings'],
    [...ISSUE_EXPORT_COLUMNS],
    ...rows,
  ])}`;
}

export function downloadManualIssuesCsv(
  issues: ManualIssueListItem[],
  scopeName: string,
  date: Date = new Date(),
): string {
  const csv = buildManualIssuesCsv(issues, date);
  const fileDate = date.toISOString().slice(0, 10);
  const filename = `ablecomply-issues-${slugifyFilePart(scopeName)}-${fileDate}.csv`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
  return filename;
}
